import { html, cx, useState, useEffect, useRef, useCallback } from '../h.js'
import { Icon } from '../icons.js'
import { t } from '../i18n.js'
import { duration, shortId } from '../format.js'
import { Avatar, useMenu, useToast } from '../components.js'
import { tones } from '../tones.js'
import { useApp, useStore } from '../store.js'
import { Call } from '@pine/core'

/**
 * Somebody is calling.
 *
 * On a computer: a banner in the corner, so the app stays usable around it.
 * On a phone: the whole screen, the way every phone shows a call. It used to
 * be the banner there too, and a phone woken by a call — screen off, lying on
 * the table — lit up on whatever chat had been open last, with a small card
 * over it that was easy to miss; over the lock screen it showed that chat to
 * whoever picked the phone up. The full screen covers everything behind it.
 */
export function IncomingCall () {
  const store = useStore()
  const call = store.get('incomingCall')
  const [ringing, setRinging] = useState(true)

  /*
   * The ringtone plays here only where the platform does not ring by itself.
   *
   * Android plays the real ringtone natively (CallRinger) whether the app is
   * in front or not, and a second sound layered under it is just noise. The
   * web and desktop builds ring from the page — the browser's own call
   * notification, when there is one, is silent.
   */
  useEffect(() => {
    if (!call || (store.platform && store.platform.ringsNatively)) return
    tones.incoming()
    return () => { tones.stop(); setRinging(false) }
  }, [call, store])
  void ringing

  if (!call) return null

  const summary = store.client.getSummary(call.roomId) || {}

  if (onPhone(store)) {
    return html`
      <div class="incoming-full" role="dialog" aria-modal="true"
           aria-label=${call.video ? t('call.incomingVideo') : t('call.incoming')}>
        <div class="incoming-who">
          <${Avatar} name=${summary.name} id=${call.roomId} src=${summary.avatar || null} size="xl" />
          <h2>${summary.name || shortId(call.roomId)}</h2>
          <p>${call.video ? t('call.incomingVideo') : t('call.incoming')}</p>
        </div>
        <div class="incoming-actions">
          <div class="incoming-action">
            <button class="call-btn hangup" aria-label=${t('call.decline')} onClick=${() => store.calls.decline()}>
              ${Icon.phoneOff(28)}
            </button>
            <span>${t('call.decline')}</span>
          </div>
          <div class="incoming-action">
            <button class="call-btn answer" aria-label=${t('call.accept')} onClick=${() => store.calls.accept()}>
              ${call.video ? Icon.camera(28) : Icon.phone(28)}
            </button>
            <span>${t('call.accept')}</span>
          </div>
        </div>
      </div>`
  }

  return html`
    <div class="incoming">
      <div class="row gap3">
        <${Avatar} name=${summary.name} id=${call.roomId} size="lg" />
        <div class="grow" style="min-width:0">
          <div class="strong truncate">${summary.name || shortId(call.roomId)}</div>
          <div class="small dim">${call.video ? t('call.incomingVideo') : t('call.incoming')}</div>
        </div>
      </div>
      <div class="row gap2" style="margin-top:16px">
        <button class="btn btn-danger grow" onClick=${() => store.calls.decline()}>
          ${Icon.phoneOff(18)} ${t('call.decline')}
        </button>
        <button class="btn btn-primary grow" onClick=${() => store.calls.accept()}>
          ${Icon.phone(18)} ${t('call.accept')}
        </button>
      </div>
    </div>`
}

/** A phone, held in a hand: the Android app, or a narrow touch screen. */
function onPhone (store) {
  if (store.platform && store.platform.kind === 'android') return true
  return typeof matchMedia === 'function' &&
    matchMedia('(pointer: coarse)').matches &&
    matchMedia('(max-width: 900px)').matches
}

export function CallOverlay () {
  const store = useStore()
  const call = store.get('activeCall')
  if (!call) return null
  return html`<${CallSurface} call=${call} key=${call.id} />`
}

function CallSurface ({ call }) {
  const store = useStore()
  const toast = useToast()
  const [, force] = useState(0)
  const [streams, setStreams] = useState(new Map())
  const [seconds, setSeconds] = useState(0)
  const [peerMedia, setPeerMedia] = useState(new Map())
  const [sharing, setSharing] = useState(false)
  const [cameras, setCameras] = useState(call.cameraCount)
  const [facing, setFacing] = useState(call.facingMode)
  const routed = useRef(false)
  const localVideo = useRef(null)

  const speaker = store.get('speaker')
  const minimized = store.get('callMinimized')
  const menu = useMenu()

  /*
   * Where the call is coming out of.
   *
   * A phone with a headset connected has more than two answers, so the
   * control is a menu when there is something to choose between and a plain
   * toggle when there is not. `auto` is not offered as a choice: it is what
   * the call starts on, and picking anything by hand is what leaves it.
   */
  const routes = store.get('audioRoutes')
  const outputs = (routes && routes.available) || []
  const output = (routes && routes.current) || (speaker ? 'speaker' : 'earpiece')
  const outputIcon = (route, size = 24) => {
    if (route === 'bluetooth') return Icon.bluetooth(size)
    if (route === 'wired') return Icon.headphones(size)
    if (route === 'speaker') return Icon.speaker(size)
    return Icon.earpiece(size)
  }
  const outputLabel = (route) => (routes && routes.name && route === 'bluetooth')
    ? routes.name
    : t('call.route.' + route)
  const [spot, setSpot] = useState(null) // where the card was dragged to
  const canShareScreen = Call.screenShareSupported

  // Every phone has a front and a back camera, and the count is the one thing
  // a browser gets wrong on a phone — it reports one anonymous camera until a
  // capture has been allowed. So a phone always gets the switch, and a switch
  // that turns out to have nowhere to go says so.
  const phone = (store.platform && store.platform.kind === 'android') ||
    (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches)

  /**
   * Turning the camera on moves the sound to the loudspeaker.
   *
   * Nobody holds a video call to their ear, so this is what a phone is
   * expected to do — but only until the person says otherwise. Once they have
   * pressed the speaker button themselves, their choice stands.
   */
  function toggleCamera () {
    const turningOn = call.cameraOff
    call.setCameraOff(!call.cameraOff).then(() => {
      if (turningOn && !routed.current && store.canRouteAudio) store.setSpeaker(true)
    }).catch((err) => {
      // Switching the camera on inside an audio call asks for the device and
      // renegotiates, so this can reject — most often because the person said
      // no to the permission prompt.
      toast(err && err.message ? err.message : t('err.generic'), { error: true })
    })
  }

  useEffect(() => {
    const offs = [
      call.on('devices', setCameras),
      call.on('facing', setFacing),
      call.on('stream', (account, stream) => {
        setStreams((map) => new Map(map).set(account, stream))
      }),
      call.on('peer-left', (account) => {
        setStreams((map) => {
          const next = new Map(map)
          next.delete(account)
          return next
        })
      }),
      call.on('peer-media', (account, media) => {
        setPeerMedia((map) => new Map(map).set(account, media))
      }),
      call.on('state', () => force((n) => n + 1)),
      call.on('media', () => force((n) => n + 1)),
      call.on('screen-share', setSharing),
      // A call at capacity turns newcomers away; without this the call simply
      // ended on their phone with nothing said.
      call.on('declined', (_account, reason) => {
        if (reason === 'full') toast(t('call.full'), { error: true })
      }),
      // Gave up connecting (Call._watchConnect). Said, not left as a call
      // that simply vanished after a minute of "connecting".
      call.on('unreachable', () => toast(t('call.unreachable'), { error: true, ms: 8000 })),
      call.on('tick', setSeconds),
      call.on('local-stream', (stream) => attachStream(localVideo.current, stream))
    ]
    attachStream(localVideo.current, call.localStream)
    // Counted again once the call is up: before any camera permission the
    // browser reports a single anonymous camera, and the count that decides
    // whether there is a switch button has to be the real one.
    call.countCameras().catch(() => {})
    return () => offs.forEach((off) => off())
  }, [call])

  /*
   * The preview's ref is a stable function, not an arrow written inline.
   *
   * Inline, it was a new function on every render, and Preact calls a changed
   * ref again — so every re-render reassigned srcObject. That is not a no-op:
   * assigning a stream, even the same one, restarts the element's load, and a
   * restarting <video> on Android shows its grey play-button placeholder
   * until the first frame comes back. The call surface re-renders on the
   * duration tick and on every store change, which is why the picture kept
   * blinking into a play symbol every few seconds.
   */
  const bindLocal = useCallback((node) => {
    localVideo.current = node
    attachStream(node, call.localStream)
  }, [call])

  /**
   * Drag the minimised card out of the way; a tap without a drag opens the
   * call again. Buttons on the card do their own thing and are left alone.
   */
  function startDrag (event) {
    if (!minimized || (event.target.closest && event.target.closest('button'))) return
    const card = event.currentTarget
    const rect = card.getBoundingClientRect()
    const startX = event.clientX
    const startY = event.clientY
    const origin = { right: innerWidth - rect.right, bottom: innerHeight - rect.bottom }
    let moved = false

    const move = (e) => {
      const dx = e.clientX - startX
      const dy = e.clientY - startY
      if (!moved && Math.hypot(dx, dy) < 6) return
      moved = true
      setSpot({
        x: clamp(origin.right - dx, 8, innerWidth - rect.width - 8),
        y: clamp(origin.bottom - dy, 8, innerHeight - rect.height - 8)
      })
    }
    const up = () => {
      removeEventListener('pointermove', move)
      removeEventListener('pointerup', up)
      removeEventListener('pointercancel', up)
      if (!moved) store.restoreCall()
    }
    addEventListener('pointermove', move)
    addEventListener('pointerup', up)
    addEventListener('pointercancel', up)
  }

  const summary = store.client.getSummary(call.roomId) || {}
  const remotes = [...streams.entries()]
  const count = Math.max(1, remotes.length)

  /*
   * Past six tiles the fixed columns stop working — a seventh person would be
   * drawn off the bottom of the screen — so a crowd gets a grid that fits
   * itself to the count and scrolls if it has to. Thirty is the ceiling; see
   * MESH_LIMIT.
   */
  const grid = count <= 6 ? 'n' + count : 'many'

  const statusLine = call.state === 'dialling'
    ? t('call.calling')
    : call.state === 'connecting' || remotes.length === 0
      ? t('call.connecting')
      : call.group
        // In a group the clock alone says nothing about who turned up.
        ? duration(seconds) + ' · ' + t('call.participants', { n: remotes.length + 1 })
        : duration(seconds)

  return html`
    <div class=${cx('call-overlay', minimized && 'minimized')}
         style=${minimized && spot ? { right: spot.x + 'px', bottom: spot.y + 'px' } : null}
         onPointerDown=${startDrag}>
      <div class="call-status">
        <button class="call-min-btn" title=${t('call.minimize')} aria-label=${t('call.minimize')}
                onClick=${() => store.minimizeCall()}>
          ${Icon.chevronDown(22)}
        </button>
        <h2>${summary.name || shortId(call.roomId)}</h2>
        <p>${statusLine}</p>
      </div>

      <div class=${cx('call-grid', grid)}>
        ${remotes.length === 0
          ? html`
            <div class="call-tile">
              <${Avatar} name=${summary.name} id=${call.roomId} size="xl" />
            </div>`
          : remotes.map(([account, stream]) => html`
            <${RemoteTile} key=${account} account=${account} stream=${stream}
              media=${peerMedia.get(account)} roomId=${call.roomId} />`)}
      </div>

      ${call.video && html`
        <!-- Mirrored only for a front camera. Your own face is expected to
             behave like a mirror; the back camera is a window, and flipping
             what it sees makes every sign and label in the room read
             backwards. -->
        <div class=${cx('call-self', facing !== 'environment' && 'mirrored')}>
          <video ref=${bindLocal} autoplay playsinline muted></video>
        </div>`}

      <div class="call-bar">
        <button class=${cx('call-btn', call.muted && 'off')} title=${t('call.mute')}
                onClick=${() => call.setMuted(!call.muted)}>
          ${call.muted ? Icon.micOff(24) : Icon.mic(24)}
        </button>

        ${/*
          * Lit when the loudspeaker is on, not when it is off: the earpiece is
          * the ordinary state of a call, and `.off` is what this bar uses for
          * a control that has been switched off — a muted microphone, a
          * stopped camera. An earpiece drawn that way reads as a fault.
          */
        store.canRouteAudio && html`
          <button class=${cx('call-btn', output !== 'earpiece' && 'active')}
                  title=${t('call.audioOutput') + ': ' + outputLabel(output)}
                  onClick=${(event) => {
                    routed.current = true
                    if (outputs.length > 2) {
                      menu.open(event, outputs.map((route) => ({
                        label: outputLabel(route),
                        icon: outputIcon(route, 17),
                        onSelect: () => store.setAudioRoute(route)
                      })))
                    } else {
                      store.setAudioRoute(output === 'speaker' ? 'earpiece' : 'speaker')
                    }
                  }}>
            ${outputIcon(output)}
          </button>`}

        <button class=${cx('call-btn', call.cameraOff && 'off')} title=${t('call.camera')}
                onClick=${toggleCamera}>
          ${call.cameraOff ? Icon.cameraOff(24) : Icon.camera(24)}
        </button>

        ${!call.cameraOff && !sharing && (Math.max(cameras, call.cameraCount) > 1 || phone) && html`
          <button class="call-btn" title=${t('call.flip')}
                  onClick=${() => {
                    call.switchCamera().catch((err) => {
                      toast(err && err.message ? err.message : t('err.generic'), { error: true })
                    })
                  }}>
            ${Icon.cameraSwitch(24)}
          </button>`}

        ${canShareScreen && html`
          <button class=${cx('call-btn', sharing && 'active')} title=${t('call.screen')}
                  aria-pressed=${String(sharing)}
                  onClick=${() => {
                    // Picking a screen is a native dialog and can be cancelled,
                    // which arrives here as a rejection; only say something
                    // when it is a real failure.
                    call.setScreenShare(!sharing).catch((err) => {
                      if (err && err.name === 'NotAllowedError') return
                      toast(err && err.message ? err.message : t('err.generic'), { error: true })
                    })
                  }}>
            ${Icon.screen(24)}
          </button>`}

        <button class="call-btn hangup" title=${t('call.hangUp')} onClick=${() => call.hangUp()}>
          ${Icon.phoneOff(26)}
        </button>
      </div>

      <div class="call-mini">
        <div class="call-mini-info">
          <span class="truncate strong">${summary.name || shortId(call.roomId)}</span>
          <span class="dim">${statusLine}</span>
        </div>
        <button class=${cx('call-btn sm', call.muted && 'off')} title=${t('call.mute')}
                onClick=${() => call.setMuted(!call.muted)}>
          ${call.muted ? Icon.micOff(17) : Icon.mic(17)}
        </button>
        <button class="call-btn sm hangup" title=${t('call.hangUp')} onClick=${() => call.hangUp()}>
          ${Icon.phoneOff(17)}
        </button>
      </div>

      ${menu.node}
    </div>`
}

function clamp (value, low, high) {
  return Math.max(low, Math.min(high, value))
}

function RemoteTile ({ account, stream, media, roomId }) {
  const store = useApp()
  const ref = useRef(null)
  const [name, setName] = useState('')
  const [speaking, setSpeaking] = useState(false)

  useEffect(() => { attachStream(ref.current, stream) }, [stream])

  useEffect(() => {
    let alive = true
    store.client.openRoom(roomId).then((room) => room.view.member(account)).then((member) => {
      if (alive && member) setName(member.name || shortId(account))
    }).catch(() => {})
    return () => { alive = false }
  }, [account, roomId, store])

  // Light-touch speaking detection so the active speaker is obvious in a grid.
  useEffect(() => {
    if (!stream || !stream.getAudioTracks().length) return
    let raf = 0
    const context = new (window.AudioContext || window.webkitAudioContext)()
    const analyser = context.createAnalyser()
    analyser.fftSize = 512
    context.createMediaStreamSource(stream).connect(analyser)
    const buffer = new Float32Array(analyser.fftSize)

    const tick = () => {
      analyser.getFloatTimeDomainData(buffer)
      let sum = 0
      for (const v of buffer) sum += v * v
      setSpeaking(Math.sqrt(sum / buffer.length) > 0.02)
      raf = requestAnimationFrame(tick)
    }
    tick()
    return () => {
      cancelAnimationFrame(raf)
      context.close().catch(() => {})
    }
  }, [stream])

  const hasVideo = stream && stream.getVideoTracks().some((track) => track.enabled)
  const cameraOff = media && media.cameraOff

  return html`
    <div class=${cx('call-tile', speaking && 'speaking')}>
      <video ref=${ref} autoplay playsinline class=${cx((!hasVideo || cameraOff) && 'hidden')}></video>
      ${(!hasVideo || cameraOff) && html`<${Avatar} name=${name} id=${account} size="lg" />`}
      <div class="name">
        ${media && media.muted && Icon.micOff(13)}
        <span>${name || shortId(account)}</span>
      </div>
    </div>`
}

/**
 * Give a <video> its stream — only if it does not already have it.
 *
 * Reassigning srcObject always restarts the element's load, even for the same
 * stream, and on Android a restarting video shows the grey play-button
 * placeholder until frames resume.
 */
function attachStream (node, stream) {
  if (!node) return
  if (node.srcObject !== (stream || null)) node.srcObject = stream || null
}
